import {
  IconChevronDown,
  IconInfoCircle,
  IconLoader2,
  IconPlayerPauseFilled,
  IconPlayerPlayFilled,
  IconRewindBackward15,
  IconRewindForward15,
  IconX,
} from "@tabler/icons-react";
import { AnimatePresence, LayoutGroup, motion } from "motion/react";
import type { Variants } from "motion/react";
import { cn } from "../../lib/utils";

type AudioPlayerState = "playing" | "paused" | "loading";

type AudioPlayerProps = {
  title: string;
  /** Journal / source line under the title. Hidden while `state` is "loading". */
  source: string;
  /** Cover artwork shown in the expanded sheet. `null` hides the artwork. */
  imageUrl: string | null;
  state: AudioPlayerState;
  /** Playback fraction in [0, 1]. `null` hides the collapsed progress line. */
  progress: number | null;
  /** Normalized amplitude envelope (0..1 per bar) of the real track. */
  waveform: number[];
  onPlayPause: () => void;
  onClose: () => void;
  /** Tap the collapsed card body to expand; `null` disables it. */
  onExpand: (() => void) | null;
  /** When true, the animated expanded sheet overlays on top of the mini card. */
  expanded: boolean;
  onCollapse: () => void;
  /**
   * Publication metadata shown inline (in place of the cover) when `infoOpen`.
   * `null` hides the header info button entirely.
   */
  info: React.ReactNode;
  /** When true, the sheet swaps its cover for the `info` block. */
  infoOpen: boolean;
  /** Toggle the inline info view from the header info button. */
  onToggleInfo: () => void;
  /** Elapsed time label, e.g. "1:47". */
  currentLabel: string;
  /** Total duration label, e.g. "4:12". */
  durationLabel: string;
  /** Seek to a fraction in [0, 1] of the track. */
  onSeek: (fraction: number) => void;
  /** null disables the button: a live stream has nothing to skip into yet. */
  onSkipBack: (() => void) | null;
  onSkipForward: (() => void) | null;
  /** Playback speed label, e.g. "1× speed". */
  speedLabel: string;
  onCycleSpeed: () => void;
  className?: string;
};

// Bar pixel heights the normalized amplitudes (0..1) map onto.
const WAVEFORM_MIN_PX = 6;
const WAVEFORM_MAX_PX = 36;

// The collapsed card and the expanded sheet are the two faces of ONE surface
// sharing this `layoutId`: motion morphs the box geometry (position, size,
// radius) between them on toggle rather than cross-fading two elements. A
// single spring drives the shell, its inner content repositioning and the
// morph in both directions, so nothing lags or settles at a different rate.
const SURFACE_LAYOUT_ID = "audio-player-surface";
const SURFACE_SPRING = {
  type: "spring",
  stiffness: 300,
  damping: 34,
  mass: 1,
} as const;

// Corner radii (px) — set via `style` so motion interpolates them across the
// morph rather than snapping between Tailwind rounding classes.
const MINI_RADIUS_PX = 16;
const SHEET_RADIUS_PX = 24;
// Cover artwork in the expanded sheet — 150×150 with a 22px radius (DS 5.0).
const COVER_RADIUS_PX = 22;

// The expanded content enters as a staggered cascade: each item drops in from
// slightly below while the shell springs open. Items start hidden, so the
// fastest first frames of the morph carry no text to smear.
const CONTENT_EASE_OUT = [0.22, 1, 0.36, 1] as const;
const contentContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.045, delayChildren: 0.12 } },
  exit: { transition: { staggerChildren: 0.03, staggerDirection: -1 } },
};
const contentItem: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: CONTENT_EASE_OUT },
  },
  exit: { opacity: 0, y: 6, transition: { duration: 0.15, ease: "easeIn" } },
};

/**
 * Audio player for article narration. Collapsed, it docks as a mini card
 * (title + source, play/pause, close, slim progress line). Tapping the body
 * morphs it into an animated bottom sheet with waveform, scrubbing, ±15s
 * skips and speed control. Design System 5.0 — Audio Player.
 *
 * The collapsed card and expanded sheet share a `layoutId`, so motion springs
 * the box geometry between them; the sheet's content cascades in over the
 * morph, the waveform's played bars pulse during playback, and the sheet can
 * be flicked down to dismiss.
 */
export function AudioPlayer({
  title,
  source,
  imageUrl,
  state,
  progress,
  waveform,
  onPlayPause,
  onClose,
  onExpand,
  expanded,
  onCollapse,
  info,
  infoOpen,
  onToggleInfo,
  currentLabel,
  durationLabel,
  onSeek,
  onSkipBack,
  onSkipForward,
  speedLabel,
  onCycleSpeed,
  className,
}: AudioPlayerProps): React.ReactElement {
  const clampedProgress =
    progress === null ? null : Math.min(Math.max(progress, 0), 1);
  const fillFraction =
    state === "loading" || clampedProgress === null ? 0 : clampedProgress;

  const playedBars =
    clampedProgress === null
      ? 0
      : Math.round(clampedProgress * waveform.length);

  function playPauseIcon(size: number): React.ReactElement {
    switch (state) {
      case "playing":
        return <IconPlayerPauseFilled size={size} />;
      case "paused":
        return <IconPlayerPlayFilled size={size} />;
      case "loading":
        return <IconLoader2 size={size} className="animate-spin" />;
      default:
        throw new Error(state satisfies never);
    }
  }

  return (
    <LayoutGroup>
      <AnimatePresence>
        {expanded && (
          <motion.div
            key="audio-backdrop"
            className="fixed inset-0 z-60 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onCollapse}
          />
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {expanded ? (
          <div
            key="audio-expanded"
            className="fixed inset-x-2 z-60 mx-auto max-w-150"
            style={{ bottom: "calc(var(--safe-area-bottom) + 8px)" }}
          >
            <motion.div
              layoutId={SURFACE_LAYOUT_ID}
              layout
              transition={SURFACE_SPRING}
              drag="y"
              dragConstraints={{ top: 0, bottom: 0 }}
              dragElastic={{ top: 0, bottom: 0.6 }}
              onDragEnd={(_, info) => {
                if (info.offset.y > 90 || info.velocity.y > 600) onCollapse();
              }}
              style={{ borderRadius: SHEET_RADIUS_PX }}
              className={cn(
                "relative w-full touch-none overflow-hidden",
                "border border-solid border-[rgba(11,17,16,0.1)]",
                "bg-[rgba(246,245,243,0.9)] backdrop-blur-md",
                "shadow-[0px_21px_13px_0px_rgba(98,85,85,0.05),0px_9px_9px_0px_rgba(98,85,85,0.09),0px_2px_5px_0px_rgba(98,85,85,0.1)]"
              )}
            >
              <motion.div
                layout="position"
                transition={SURFACE_SPRING}
                variants={contentContainer}
                initial="hidden"
                animate="show"
                exit="exit"
                className="relative flex w-full flex-col items-center gap-5 px-4 pt-3 pb-6"
              >
                <motion.div
                  variants={contentItem}
                  className="h-1.25 w-10 shrink-0 rounded-full bg-[#e0d8d3]"
                />

                <motion.div
                  variants={contentItem}
                  className="flex w-full items-center justify-between"
                >
                  <span className="font-dm-sans text-caption font-semibold uppercase tracking-[0.66px] text-ds-slate-500">
                    Now playing
                  </span>
                  <div className="flex items-center gap-1.5">
                    {info === null ? null : (
                      <motion.button
                        whileTap={{ scale: 0.85 }}
                        type="button"
                        aria-label="Information"
                        aria-pressed={infoOpen}
                        onClick={onToggleInfo}
                        className={cn(
                          "flex size-8 items-center justify-center rounded-2xl outline-none cursor-pointer transition-colors",
                          infoOpen
                            ? "bg-ds-slate-950 text-ds-white"
                            : "text-ds-slate-500"
                        )}
                      >
                        <IconInfoCircle size={20} />
                      </motion.button>
                    )}
                    <motion.button
                      whileTap={{ scale: 0.85 }}
                      type="button"
                      aria-label="Collapse"
                      onClick={onCollapse}
                      className="flex size-8 items-center justify-center rounded-2xl text-ds-slate-500 outline-none cursor-pointer"
                    >
                      <IconChevronDown size={22} />
                    </motion.button>
                  </div>
                </motion.div>

                <AnimatePresence mode="popLayout" initial={false}>
                  {imageUrl !== null && (
                    <motion.div
                      key="cover"
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.96 }}
                      transition={{ duration: 0.25, ease: CONTENT_EASE_OUT }}
                      className="size-37.5 shrink-0 overflow-hidden"
                      style={{ borderRadius: COVER_RADIUS_PX }}
                    >
                      <img
                        src={imageUrl}
                        alt=""
                        className="size-full object-cover"
                      />
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div
                  variants={contentItem}
                  className="flex w-full flex-col items-center gap-1 text-center"
                >
                  <p className="line-clamp-3 font-dm-sans text-heading-xs font-semibold text-ds-slate-950">
                    {title}
                  </p>
                  <p className="truncate font-dm-sans text-footnote text-ds-slate-500">
                    {source}
                  </p>
                </motion.div>

                <AnimatePresence mode="popLayout" initial={false}>
                  {infoOpen && info !== null && (
                    <motion.div
                      key="info"
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.28, ease: CONTENT_EASE_OUT }}
                      className="w-full"
                    >
                      {info}
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div
                  variants={contentItem}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const fraction = (e.clientX - rect.left) / rect.width;
                    onSeek(Math.min(Math.max(fraction, 0), 1));
                  }}
                  className="flex h-11 w-full cursor-pointer items-center justify-between"
                >
                  {waveform.map((amplitude, i) => {
                    const played = i < playedBars;
                    const active = played && state === "playing";
                    return (
                      <motion.div
                        key={i}
                        className={cn(
                          "w-0.75 shrink-0 rounded-[1.5px]",
                          played ? "bg-ds-mint-800" : "bg-[#dcd4cf]"
                        )}
                        style={{
                          height: `${WAVEFORM_MIN_PX + amplitude * (WAVEFORM_MAX_PX - WAVEFORM_MIN_PX)}px`,
                          transformOrigin: "center",
                        }}
                        animate={{ scaleY: active ? [1, 0.5, 1] : 1 }}
                        transition={
                          active
                            ? {
                                duration: 0.85 + (i % 5) * 0.14,
                                repeat: Infinity,
                                repeatType: "mirror",
                                ease: "easeInOut",
                                delay: (i % 7) * 0.09,
                              }
                            : { duration: 0.35, ease: "easeOut" }
                        }
                      />
                    );
                  })}
                </motion.div>

                <motion.div
                  variants={contentItem}
                  className="flex w-full justify-between font-dm-sans text-caption font-medium text-ds-slate-500"
                >
                  <span>{currentLabel}</span>
                  <span>{durationLabel}</span>
                </motion.div>

                <motion.div
                  variants={contentItem}
                  className="flex items-center gap-7"
                >
                  <motion.button
                    whileTap={onSkipBack === null ? undefined : { scale: 0.88 }}
                    type="button"
                    aria-label="Skip back 15 seconds"
                    onClick={() => {
                      if (onSkipBack !== null) onSkipBack();
                    }}
                    disabled={onSkipBack === null}
                    className="flex size-10 items-center justify-center rounded-full text-ds-slate-950 outline-none cursor-pointer disabled:cursor-default disabled:opacity-30"
                  >
                    <IconRewindBackward15 size={30} />
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.92 }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                    type="button"
                    aria-label={state === "playing" ? "Pause" : "Play"}
                    onClick={onPlayPause}
                    className="flex size-16.5 items-center justify-center rounded-full bg-[rgba(11,17,16,0.24)] text-ds-black outline-none cursor-pointer"
                  >
                    {playPauseIcon(29)}
                  </motion.button>
                  <motion.button
                    whileTap={
                      onSkipForward === null ? undefined : { scale: 0.88 }
                    }
                    type="button"
                    aria-label="Skip forward 15 seconds"
                    onClick={() => {
                      if (onSkipForward !== null) onSkipForward();
                    }}
                    disabled={onSkipForward === null}
                    className="flex size-10 items-center justify-center rounded-full text-ds-slate-950 outline-none cursor-pointer disabled:cursor-default disabled:opacity-30"
                  >
                    <IconRewindForward15 size={30} />
                  </motion.button>
                </motion.div>

                <motion.button
                  variants={contentItem}
                  whileTap={{ scale: 0.95 }}
                  type="button"
                  onClick={onCycleSpeed}
                  className="rounded-full border border-solid border-ds-slate-200 px-3 py-1.5 font-dm-sans text-footnote font-medium text-ds-slate-800 outline-none cursor-pointer"
                >
                  {speedLabel}
                </motion.button>
              </motion.div>
            </motion.div>
          </div>
        ) : (
          <div
            key="audio-mini"
            onClick={onExpand === null ? undefined : onExpand}
            className={cn(
              "relative w-full",
              onExpand === null ? null : "cursor-pointer",
              className
            )}
          >
            <motion.div
              layoutId={SURFACE_LAYOUT_ID}
              layout
              transition={SURFACE_SPRING}
              whileTap={onExpand === null ? undefined : { scale: 0.975 }}
              style={{ borderRadius: MINI_RADIUS_PX }}
              className={cn(
                "relative w-full overflow-hidden",
                "border border-solid border-ds-slate-200",
                "bg-[rgba(246,245,243,0.68)] backdrop-blur-lg",
                "shadow-[0px_9px_9px_0px_rgba(98,85,85,0.09),0px_2px_5px_0px_rgba(98,85,85,0.1)]"
              )}
            >
              <motion.div
                layout="position"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{
                  layout: SURFACE_SPRING,
                  opacity: { duration: 0.2, delay: 0.1 },
                }}
                className="relative flex w-full items-center gap-3 px-4 py-2"
              >
                <div className="flex min-w-0 flex-1 flex-col gap-px overflow-hidden">
                  <p className="truncate font-dm-sans text-ui-body font-semibold text-ds-slate-950">
                    {title}
                  </p>
                  <p className="truncate font-dm-sans text-footnote text-ds-slate-500">
                    {state === "loading" ? "Loading…" : source}
                  </p>
                </div>

                <div className="flex shrink-0 items-center gap-0.5">
                  <motion.button
                    whileTap={{ scale: 0.85 }}
                    type="button"
                    aria-label={state === "playing" ? "Pause" : "Play"}
                    onClick={(e) => {
                      e.stopPropagation();
                      onPlayPause();
                    }}
                    className="flex size-10 shrink-0 items-center justify-center rounded-full text-ds-mint-900 outline-none cursor-pointer"
                  >
                    {playPauseIcon(26)}
                  </motion.button>
                  <motion.button
                    whileTap={{ scale: 0.85 }}
                    type="button"
                    aria-label="Close"
                    onClick={(e) => {
                      e.stopPropagation();
                      onClose();
                    }}
                    className="flex size-10 shrink-0 items-center justify-center rounded-full text-ds-black outline-none cursor-pointer"
                  >
                    <IconX size={24} />
                  </motion.button>
                </div>
              </motion.div>

              {clampedProgress === null ? null : (
                <div className="absolute inset-x-0 bottom-0 h-0.75 overflow-hidden bg-ds-black/6">
                  <div
                    className="h-full bg-ds-black/[0.14]"
                    style={{ width: `${fillFraction * 100}%` }}
                  />
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </LayoutGroup>
  );
}

export { type AudioPlayerProps, type AudioPlayerState };
